/**
 * @file PolicyTable.jsx
 * @description PolicyTable component for displaying and managing policy table.
 * @component PolicyTable
 * @exports PolicyTable - Main component for displaying and managing policy table.
 */

import React from "react";
import Table from "@mui/material/Table";
import { Edit } from "@mui/icons-material";
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import IconButton from "@mui/material/IconButton";
import TableSortLabel from "@mui/material/TableSortLabel";
import TableContainer from "@mui/material/TableContainer";
import LinearProgress from "@mui/material/LinearProgress";
import TablePagination from "@mui/material/TablePagination";
import { SIZES, SORT_ORDERS, ALIGN, HTML_TAGS } from "../../../common/constants/commonConstants.js";
import { POLICY_TABLE_COLUMN_KEYS, POLICY_MESSAGES } from "../../../common/constants/ivrModule/policyConstants.js";

const PolicyTable = ({
    page,
    order,
    setPage,
    orderBy,
    policy,
    rowsPerPage,
    columns,
    isPolicyLoading,
    setRowsPerPage,
    onEdit,
    handleRequestSort,
    filteredPolicyLength
}) => {
    return (
        <>
            <TableContainer sx={{ mt: 2 }}>
                <Table size={SIZES.SMALL}>
                    <TableHead>
                        <TableRow>
                            {columns.map((column) => (
                                <TableCell
                                    key={column.id}
                                    sortDirection={orderBy === column.id ? order : false}
                                    align={column.id === POLICY_TABLE_COLUMN_KEYS.OFFER_EXPIRES ? ALIGN.CENTER : 
                                        column.id === POLICY_TABLE_COLUMN_KEYS.MIN_OFFERS ? ALIGN.CENTER : 
                                        column.id === POLICY_TABLE_COLUMN_KEYS.MAX_OFFERS ? ALIGN.CENTER :
                                        ALIGN.LEFT}
                                    sx={{
                                        width:
                                            column.id === POLICY_TABLE_COLUMN_KEYS.ACTIONS ? 50 :
                                            column.id === POLICY_TABLE_COLUMN_KEYS.ID ? 250 :
                                            column.id === POLICY_TABLE_COLUMN_KEYS.POLICY_NAME ? 250 :
                                            column.id === POLICY_TABLE_COLUMN_KEYS.MODE_KIND ? 175 :
                                            column.id === POLICY_TABLE_COLUMN_KEYS.OFFER_EXPIRES ? 150 :
                                            column.id === POLICY_TABLE_COLUMN_KEYS.MIN_OFFERS ? 150 :
                                            column.id === POLICY_TABLE_COLUMN_KEYS.MAX_OFFERS ? 150 : 200,
                                    }}
                                >
                                    {column.isSortable ? (
                                        <TableSortLabel
                                            active={orderBy === column.id}
                                            direction={orderBy === column.id ? order : SORT_ORDERS.ASCENDING}
                                            onClick={() =>
                                                handleRequestSort(column.id)
                                            }
                                        >
                                            {column.label}
                                        </TableSortLabel>
                                    ) : column.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isPolicyLoading ? (
                            <TableRow>
                                <TableCell colSpan={columns.length} align={ALIGN.CENTER} >
                                    <LinearProgress />
                                </TableCell>
                            </TableRow>
                        ) : policy.length > 0 ? policy.map((policy) => (
                            <TableRow hover tabIndex={-1} key={policy.id}>
                                <TableCell>
                                    <IconButton onClick={() => onEdit(policy)}>
                                        <Edit fontSize={SIZES.SMALL} />
                                    </IconButton>
                                </TableCell>
                                <TableCell>
                                    {policy.id}
                                </TableCell>
                                <TableCell>
                                    {policy.policyname}
                                </TableCell>
                                <TableCell>
                                    {policy.modeKind}
                                </TableCell>
                                <TableCell align="center">
                                    {policy.offerExpiresAfterSeconds}
                                </TableCell>
                                <TableCell align="center">
                                    {policy.minConcurrentOffers}
                                </TableCell>
                                <TableCell align="center">
                                    {policy.maxConcurrentOffers}
                                </TableCell>
                            </TableRow>
                        )) : (
                            <TableRow>
                                <TableCell colSpan={columns.length} align={ALIGN.CENTER}>
                                    {POLICY_MESSAGES.NO_POLICY}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    page={page}
                    rowsPerPage={rowsPerPage}
                    component={HTML_TAGS.DIV}
                    count={filteredPolicyLength}
                    rowsPerPageOptions={[10, 25, 50]}
                    onPageChange={(event, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(event) => {
                        setPage(0);
                        setRowsPerPage(parseInt(event.target.value, 10));
                    }}
                />
            </TableContainer>
        </>
    )
};

export default PolicyTable;